import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Share2,
  Trash2,
  Copy,
  Clock,
  User,
  Music,
  Link,
  Search,
  Filter,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Expand,
  Minimize,
  MoreHorizontal,
  Edit,
  RefreshCw,
  AlertCircle,
  ArrowUpDown,
  CheckSquare,
  Square,
  Play,
  Timer
} from 'lucide-react';
import { usePractitionerSessions } from '@/hooks/usePractitionerSessions';
import { usePractitionerClients } from '@/hooks/usePractitionerClients';
import { useSessionMutations } from '@/hooks/mutations/useSessionMutations';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/utils/apiClient';
import { formatDistanceToNow } from 'date-fns';
import type { SessionRecord } from '@/types/session';
import { categories } from '@/config/categories';
import LoadingSpinner from '@/components/LoadingSpinner';
import EnhancedAudioPlayer from '@/components/EnhancedAudioPlayer';
import { AudioPlayerCoordinatorProvider } from '@/contexts/AudioPlayerCoordinator';
import { ClientFeedbackDisplay } from '@/components/practitioner/ClientFeedbackDisplay';
import { useCollapsiblePreferences } from '@/hooks/useCollapsiblePreferences';

export const PractitionerAllSessionsPage = () => {
  const { clientSessions, sharedLinks, loading, createSharedLink, fetchClientSessions } = usePractitionerSessions();
  const { clients } = usePractitionerClients();
  const { toast } = useToast();
  const {
    deleteSession,
    deleteSessionsBulk,
    isDeleting
  } = useSessionMutations();

  // Collapsible preferences
  const { preferences, updateFiltersCollapsed, updateSessionGroupCollapsed, collapseAll, expandAll } = useCollapsiblePreferences();

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'date' | 'title' | 'category' | 'client'>('date');

  // Selection state
  const [selectedSessions, setSelectedSessions] = useState<Set<string>>(new Set());

  // Edit state
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingSessionName, setEditingSessionName] = useState<string>('');

  // Retry state
  const [retryingSessionId, setRetryingSessionId] = useState<string | null>(null);

  // Refresh data on component mount
  useEffect(() => {
    fetchClientSessions();
  }, []);

  const getClientName = (clientId?: string) => {
    if (!clientId) return 'No Client';
    const client = clients.find(c => c.id === clientId);
    return client?.client_name || 'Unknown Client';
  };

  const getCategoryName = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || categoryId;
  };

  const getSharedLinkForSession = (sessionId: string) => {
    return sharedLinks.find(link => link.session_id === sessionId && link.is_active);
  };

  const hasFeedback = (session: SessionRecord) => {
    if (!session.client_feedback) return false;
    
    // Handle case where client_feedback might be a string (legacy format)
    if (typeof session.client_feedback === 'string') {
      return (session.client_feedback as string).trim().length > 0;
    }
    
    // Handle object format
    if (typeof session.client_feedback === 'object' && session.client_feedback !== null) {
      return Object.keys(session.client_feedback).length > 0;
    }
    
    return false;
  };

  const getSessionStatus = (session: SessionRecord) => {
    const sharedLink = getSharedLinkForSession(session.id);

    if (!sharedLink) return 'draft';
    if (sharedLink.total_plays > 0) return 'accessed';
    // Note: Links no longer expire, removed expiration check
    return 'shared';
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      draft: { label: 'Draft', variant: 'secondary' as const },
      shared: { label: 'Shared', variant: 'default' as const },
      accessed: { label: 'Accessed', variant: 'outline' as const },
      expired: { label: 'Expired', variant: 'destructive' as const },
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.draft;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  // Filter and sort sessions
  const filteredSessions = clientSessions.filter(session => {
    const matchesSearch = session.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         getClientName(session.client_id).toLowerCase().includes(searchTerm.toLowerCase());

    const matchesClient = selectedClient === 'all' || session.client_id === selectedClient;
    const matchesCategory = selectedCategory === 'all' || session.category_id === selectedCategory;

    const sessionStatus = getSessionStatus(session);
    const matchesStatus = selectedStatus === 'all' || sessionStatus === selectedStatus;

    return matchesSearch && matchesClient && matchesCategory && matchesStatus;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'title':
        return a.title.localeCompare(b.title);
      case 'category':
        return a.category_id.localeCompare(b.category_id);
      case 'client':
        return getClientName(a.client_id).localeCompare(getClientName(b.client_id));
      case 'date':
      default:
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    }
  });

  // Group sessions by status
  const sessionsByStatus = filteredSessions.reduce((groups, session) => {
    const status = getSessionStatus(session);
    if (!groups[status]) {
      groups[status] = [];
    }
    groups[status].push(session);
    return groups;
  }, {} as Record<string, SessionRecord[]>);

  const statusOrder = ['draft', 'shared', 'accessed', 'expired'];
  const statusLabels = {
    draft: 'Draft Sessions',
    shared: 'Shared Sessions', 
    accessed: 'Accessed Sessions',
    expired: 'Expired Sessions'
  };

  const handleShareSession = async (session: SessionRecord) => {
    try {
      const shareUrl = await createSharedLink(session.id, session.client_id, 168); // 7 days default
      if (shareUrl) {
        await navigator.clipboard.writeText(shareUrl);
        toast({
          title: "Session Shared",
          description: "Share link created and copied to clipboard.",
        });
      }
    } catch (error) {
      console.error('Error sharing session:', error);
      toast({
        title: "Error",
        description: "Failed to create share link. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCopyShareLink = async (session: SessionRecord) => {
    const sharedLink = getSharedLinkForSession(session.id);
    if (sharedLink) {
      const shareUrl = `${window.location.origin}/listen/${encodeURIComponent(sharedLink.access_token)}`;
      await navigator.clipboard.writeText(shareUrl);
      toast({
        title: "Link Copied",
        description: "Share link copied to clipboard.",
      });
    }
  };

  // Bulk selection handlers
  const toggleSessionSelection = (sessionId: string) => {
    const newSelected = new Set(selectedSessions);
    if (newSelected.has(sessionId)) {
      newSelected.delete(sessionId);
    } else {
      newSelected.add(sessionId);
    }
    setSelectedSessions(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedSessions.size === filteredSessions.length && filteredSessions.length > 0) {
      setSelectedSessions(new Set());
    } else {
      setSelectedSessions(new Set(filteredSessions.map(s => s.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedSessions.size === 0) return;
    await deleteSessionsBulk(Array.from(selectedSessions));
    setSelectedSessions(new Set());
    fetchClientSessions(); // Refresh the list
  };

  const handleBulkShare = async () => {
    if (selectedSessions.size === 0) return;

    let successCount = 0;
    for (const sessionId of selectedSessions) {
      const session = clientSessions.find(s => s.id === sessionId);
      if (session && session.audio_url) {
        try {
          await createSharedLink(session.id, session.client_id, 168);
          successCount++;
        } catch (error) {
          console.error(`Failed to share session ${sessionId}:`, error);
        }
      }
    }

    toast({
      title: "Bulk Share Complete",
      description: `Shared ${successCount} of ${selectedSessions.size} sessions.`,
    });
    setSelectedSessions(new Set());
    fetchClientSessions();
  };

  // Rename session handlers
  const handleRenameSession = async (sessionId: string, newTitle: string) => {
    try {
      await apiClient.patch(`/sessions/${sessionId}`, { title: newTitle });
      toast({
        title: "Session Renamed",
        description: "Session title updated successfully.",
      });
      fetchClientSessions();
      setEditingSessionId(null);
      setEditingSessionName('');
    } catch (error) {
      console.error('Failed to rename session:', error);
      toast({
        title: "Error",
        description: "Failed to rename session. Please try again.",
        variant: "destructive",
      });
    }
  };

  const startEditingName = (session: SessionRecord) => {
    setEditingSessionId(session.id);
    setEditingSessionName(session.title);
  };

  const cancelEditingName = () => {
    setEditingSessionId(null);
    setEditingSessionName('');
  };

  // Retry failed session handler
  const handleRetrySession = async (sessionId: string) => {
    setRetryingSessionId(sessionId);
    try {
      await apiClient.post(`/sessions/${sessionId}/retry`, {});
      toast({
        title: "Retry Initiated",
        description: "Session generation has been restarted.",
      });
      fetchClientSessions();
    } catch (error) {
      console.error('Retry failed:', error);
      toast({
        title: "Retry Failed",
        description: "Could not retry session generation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setRetryingSessionId(null);
    }
  };

  // Delete single session handler
  const handleDeleteSession = async (sessionId: string) => {
    await deleteSession(sessionId, clientSessions);
    fetchClientSessions();
  };

  // Audio helpers
  const hasWorkingAudio = (session: SessionRecord) => {
    const isAudioExpired = !session.audio_url || session.audio_url.includes('blob:');
    // Sessions are ready when created or shared (shared means it was created then shared)
    const isSessionComplete = session.delivery_status === 'created' || session.delivery_status === 'shared';
    return session.audio_url && !isAudioExpired && isSessionComplete;
  };

  const hasBackgroundMusic = (session: SessionRecord) => {
    return !!session.background_music_file || !!session.background_audio_url;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <AudioPlayerCoordinatorProvider>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">All Sessions</h1>
            <p className="text-muted-foreground">
              Manage all your client sessions - drafts, shared, and accessed.
            </p>
          </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="w-fit">
            {filteredSessions.length} of {clientSessions.length} sessions
          </Badge>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={expandAll}
              className="flex items-center gap-1"
            >
              <Expand className="h-3 w-3" />
              Expand All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={collapseAll}
              className="flex items-center gap-1"
            >
              <Minimize className="h-3 w-3" />
              Collapse All
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Collapsible open={preferences.filters} onOpenChange={updateFiltersCollapsed}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4" />
                  <CardTitle className="text-lg">Filters</CardTitle>
                </div>
                {preferences.filters ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="search">Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="search"
                      placeholder="Search sessions..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="client-filter">Client</Label>
                  <Select value={selectedClient} onValueChange={setSelectedClient}>
                    <SelectTrigger>
                      <SelectValue placeholder="All clients" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Clients</SelectItem>
                      {clients.map(client => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.client_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category-filter">Category</Label>
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger>
                      <SelectValue placeholder="All categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map(category => (
                        <SelectItem key={category.id} value={category.id}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status-filter">Status</Label>
                  <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                    <SelectTrigger>
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="shared">Shared</SelectItem>
                      <SelectItem value="accessed">Accessed</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sort-by">Sort By</Label>
                  <Select value={sortBy} onValueChange={(value: 'date' | 'title' | 'category' | 'client') => setSortBy(value)}>
                    <SelectTrigger>
                      <ArrowUpDown className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Sort by..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="date">Newest First</SelectItem>
                      <SelectItem value="title">Title (A-Z)</SelectItem>
                      <SelectItem value="category">Category</SelectItem>
                      <SelectItem value="client">Client</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Bulk Actions Bar */}
      {filteredSessions.length > 0 && (
        <div className="flex items-center justify-between p-3 bg-card rounded-lg border">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleSelectAll}
              className="h-8 w-8 p-0"
            >
              {selectedSessions.size === filteredSessions.length && filteredSessions.length > 0 ? (
                <CheckSquare className="h-4 w-4 text-primary" />
              ) : (
                <Square className="h-4 w-4" />
              )}
            </Button>
            <span className="text-sm text-muted-foreground">
              {selectedSessions.size > 0 ? (
                <Badge variant="secondary">{selectedSessions.size} selected</Badge>
              ) : (
                `${filteredSessions.length} session${filteredSessions.length !== 1 ? 's' : ''}`
              )}
            </span>
          </div>

          {selectedSessions.size > 0 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkShare}
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share Selected
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleBulkDelete}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Selected
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Sessions List */}
      <div className="space-y-4">
        {filteredSessions.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Music className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No sessions found</h3>
              <p className="text-muted-foreground text-center mb-4">
                {clientSessions.length === 0 
                  ? "You haven't created any sessions yet. Create your first session to get started."
                  : "No sessions match your current filters. Try adjusting your search criteria."
                }
              </p>
              {clientSessions.length === 0 && (
                <Button asChild>
                  <a href="/practitioner/sessions">Create First Session</a>
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          statusOrder.map(status => {
            const sessionsInStatus = sessionsByStatus[status] || [];
            if (sessionsInStatus.length === 0) return null;

            return (
              <Collapsible
                key={status}
                open={preferences.sessionGroups[status]}
                onOpenChange={(open) => updateSessionGroupCollapsed(status, open)}
              >
                <Card>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-lg">
                            {statusLabels[status as keyof typeof statusLabels]}
                          </CardTitle>
                          <Badge variant="outline">
                            {sessionsInStatus.length}
                          </Badge>
                        </div>
                        {preferences.sessionGroups[status] ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="space-y-4">
                      {sessionsInStatus.map(session => {
                        const sharedLink = getSharedLinkForSession(session.id);

                        return (
                          <div key={session.id} className="relative">
                            {/* Selection Checkbox */}
                            <div className="absolute top-4 left-4 z-10">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleSessionSelection(session.id);
                                }}
                                className="h-6 w-6 p-0 bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-800 rounded border"
                              >
                                {selectedSessions.has(session.id) ? (
                                  <CheckSquare className="h-4 w-4 text-primary" />
                                ) : (
                                  <Square className="h-4 w-4" />
                                )}
                              </Button>
                            </div>

                            <Card className={`transition-all hover:shadow-md pl-8 ${selectedSessions.has(session.id) ? 'ring-2 ring-primary' : ''}`}>
                              <CardHeader className="pb-3">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1 space-y-1">
                                    {/* Title - Editable */}
                                    {editingSessionId === session.id ? (
                                      <div className="flex items-center gap-2 mb-2">
                                        <Input
                                          value={editingSessionName}
                                          onChange={(e) => setEditingSessionName(e.target.value)}
                                          placeholder="Session title..."
                                          className="h-8 text-sm"
                                          autoFocus
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                              handleRenameSession(session.id, editingSessionName);
                                            } else if (e.key === 'Escape') {
                                              cancelEditingName();
                                            }
                                          }}
                                        />
                                        <Button size="sm" onClick={() => handleRenameSession(session.id, editingSessionName)} className="h-8">
                                          Save
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={cancelEditingName} className="h-8">
                                          Cancel
                                        </Button>
                                      </div>
                                    ) : (
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <CardTitle className="text-lg">{session.title}</CardTitle>
                                        {getStatusBadge(status)}
                                        {session.delivery_status === 'pending' && (
                                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300">
                                            <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                            Generating
                                          </Badge>
                                        )}
                                        {session.delivery_status === 'failed' && (
                                          <Badge variant="destructive" className="text-xs">
                                            <AlertCircle className="h-3 w-3 mr-1" />
                                            Failed
                                            {(session.generation_retry_count ?? 0) > 0 && ` (${session.generation_retry_count}/3)`}
                                          </Badge>
                                        )}
                                        {hasBackgroundMusic(session) && (
                                          <Badge variant="secondary" className="text-xs">
                                            🎵 Background Music
                                          </Badge>
                                        )}
                                        {hasFeedback(session) && (
                                          <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100">
                                            <MessageSquare className="h-3 w-3 mr-1" />
                                            Feedback
                                          </Badge>
                                        )}
                                      </div>
                                    )}
                                    <CardDescription className="flex items-center gap-4 text-sm flex-wrap">
                                      <span className="flex items-center gap-1">
                                        <User className="h-3 w-3" />
                                        {getClientName(session.client_id)}
                                      </span>
                                      <span className="flex items-center gap-1">
                                        <Music className="h-3 w-3" />
                                        {getCategoryName(session.category_id)}
                                      </span>
                                      <span className="flex items-center gap-1">
                                        <Clock className="h-3 w-3" />
                                        {formatDistanceToNow(new Date(session.created_at), { addSuffix: true })}
                                      </span>
                                      {/* Draft expiry indicator */}
                                      {!session.is_saved && session.access_expires_at && (() => {
                                        const expiresAt = new Date(session.access_expires_at);
                                        const now = new Date();
                                        const hoursRemaining = (expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60);
                                        const isExpiringSoon = hoursRemaining < 2 && hoursRemaining > 0;

                                        if (hoursRemaining <= 0) return null;

                                        return (
                                          <span className={`flex items-center gap-1 ${isExpiringSoon ? 'text-red-500' : 'text-amber-500'}`}>
                                            <Timer className="h-3 w-3" />
                                            Expires {formatDistanceToNow(expiresAt, { addSuffix: true })}
                                          </span>
                                        );
                                      })()}
                                      {sharedLink && (
                                        <span className="flex items-center gap-1">
                                          <Link className="h-3 w-3" />
                                          {sharedLink.total_plays > 0
                                            ? `Played ${sharedLink.total_plays} times`
                                            : 'Not played yet'
                                          }
                                        </span>
                                      )}
                                    </CardDescription>
                                  </div>

                                  {/* Actions Dropdown */}
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                        <MoreHorizontal className="w-4 h-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="z-50 bg-popover border border-border shadow-lg">
                                      <DropdownMenuItem onClick={() => startEditingName(session)}>
                                        <Edit className="w-4 h-4 mr-2" />
                                        Rename Session
                                      </DropdownMenuItem>

                                      {hasWorkingAudio(session) && (
                                        <>
                                          <DropdownMenuItem onClick={() => handleShareSession(session)}>
                                            <Share2 className="w-4 h-4 mr-2" />
                                            Share Session
                                          </DropdownMenuItem>
                                          {sharedLink && (
                                            <DropdownMenuItem onClick={() => handleCopyShareLink(session)}>
                                              <Copy className="w-4 h-4 mr-2" />
                                              Copy Share Link
                                            </DropdownMenuItem>
                                          )}
                                        </>
                                      )}

                                      {session.delivery_status === 'failed' && (
                                        <DropdownMenuItem
                                          onClick={() => handleRetrySession(session.id)}
                                          disabled={retryingSessionId === session.id}
                                        >
                                          <RefreshCw className={`w-4 h-4 mr-2 ${retryingSessionId === session.id ? 'animate-spin' : ''}`} />
                                          {retryingSessionId === session.id ? 'Retrying...' : 'Retry Generation'}
                                        </DropdownMenuItem>
                                      )}

                                      <DropdownMenuItem
                                        onClick={() => handleDeleteSession(session.id)}
                                        disabled={isDeleting(session.id)}
                                        className="text-destructive"
                                      >
                                        {isDeleting(session.id) ? (
                                          <>
                                            <span className="w-4 h-4 mr-2 animate-spin border-2 border-current border-t-transparent rounded-full inline-block" />
                                            Deleting...
                                          </>
                                        ) : (
                                          <>
                                            <Trash2 className="w-4 h-4 mr-2" />
                                            Delete Session
                                          </>
                                        )}
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </CardHeader>

                              <CardContent className="space-y-3">
                                {/* Audio Player */}
                                {hasWorkingAudio(session) ? (
                                  <div className="space-y-2">
                                    <EnhancedAudioPlayer
                                      audioUrl={session.audio_url}
                                      sessionTitle={session.title}
                                      sessionId={session.id}
                                      backgroundAudioUrl={
                                        session.background_audio_url ||
                                        (session.background_music_file
                                          ? `${import.meta.env.VITE_BACKEND_URL || '/api/v1'}/background-music/files/${session.background_music_file}`
                                          : undefined)
                                      }
                                    />
                                  </div>
                                ) : session.delivery_status === 'failed' ? (
                                  <div className="p-4 bg-destructive/10 rounded-lg space-y-3">
                                    <div className="flex items-center gap-2 text-destructive">
                                      <AlertCircle className="w-4 h-4" />
                                      <span className="font-medium">Generation failed</span>
                                    </div>
                                    {session.last_error && (
                                      <p className="text-sm text-muted-foreground truncate" title={session.last_error}>
                                        {session.last_error}
                                      </p>
                                    )}
                                    <div className="flex gap-2">
                                      <Button
                                        size="sm"
                                        onClick={() => handleRetrySession(session.id)}
                                        disabled={retryingSessionId === session.id}
                                      >
                                        <RefreshCw className={`w-4 h-4 mr-2 ${retryingSessionId === session.id ? 'animate-spin' : ''}`} />
                                        {retryingSessionId === session.id ? 'Retrying...' : 'Retry'}
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="destructive"
                                        onClick={() => handleDeleteSession(session.id)}
                                        disabled={isDeleting(session.id)}
                                      >
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        Delete
                                      </Button>
                                    </div>
                                  </div>
                                ) : session.delivery_status === 'pending' ? (
                                  <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                                    <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                                      <RefreshCw className="w-4 h-4 animate-spin" />
                                      <span className="text-sm">Generating audio...</span>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center p-4 bg-muted/50 rounded-lg">
                                    <div className="flex items-center gap-2 text-muted-foreground">
                                      <Play className="w-4 h-4" />
                                      <span className="text-sm">Audio unavailable</span>
                                    </div>
                                  </div>
                                )}

                                {/* Session Notes */}
                                {session.session_notes && (
                                  <div className="p-2 bg-muted rounded text-sm">
                                    <strong>Notes:</strong> {session.session_notes}
                                  </div>
                                )}

                                {/* Client Feedback */}
                                {hasFeedback(session) && (
                                  <ClientFeedbackDisplay
                                    feedback={session.client_feedback}
                                    clientName={getClientName(session.client_id)}
                                    sessionId={session.id}
                                    feedbackStatus={session.feedback_status}
                                    onFeedbackUpdate={() => {
                                      fetchClientSessions();
                                    }}
                                  />
                                )}
                              </CardContent>
                            </Card>
                          </div>
                        );
                      })}
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            );
          })
        )}
        </div>
      </div>
    </AudioPlayerCoordinatorProvider>
  );
};